import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { IWinLevel } from "../../../abstracts/types/interfaces";
import { displayConfig, winLevels } from "../../UCConfig";
import { UCHawgSpinsScreen } from "./Screens/UCHawgSpinsScreen";
import { UCNoWinScreen } from "./Screens/UCNoWinScreen";
import { UCSpinsWinScreen } from "./Screens/UCSpinsWinScreen";
import { UCTotalWinScreen } from "./Screens/UCTotalWinScreen";
import { UCWaysWinScreen } from "./Screens/UCWaysWinScreen";
import { UCFreespinIntroScreen } from "./Screens/freespin/UCFreespinIntroScreen";
import { UCFreespinOutroScreen } from "./Screens/freespin/UCFreespinOutroScreen";
import { UCFreespinRestoreScreen } from "./Screens/freespin/UCFreespinRestoreScreen";
import { UCSuperFreespinIntroScreen } from "./Screens/superFreespin/UCSuperFreespinIntroScreen";
import { UCSuperFreespinOutroScreen } from "./Screens/superFreespin/UCSuperFreespinOutroScreen";
import { UCSuperFreespinRestoreScreen } from "./Screens/superFreespin/UCSuperFreespinRestoreScreen";

export class UCWinView extends AbstractView {
    public totalWinScreen: UCTotalWinScreen;
    public spinsWinScreen: UCSpinsWinScreen;
    public waysWinScreen: UCWaysWinScreen;
    public noWinsScreen: UCNoWinScreen;
    public hawgSpinsScreen: UCHawgSpinsScreen;

    private freespinIntroScreen: UCFreespinIntroScreen;
    private freespinRestoreScreen: UCFreespinRestoreScreen;
    private freespinOutroScreen: UCFreespinOutroScreen;
    private superFreespinIntroScreen: UCSuperFreespinIntroScreen;
    private superFreespinRestoreScreen: UCSuperFreespinRestoreScreen;
    private superFreespinOutroScreen: UCSuperFreespinOutroScreen;

    constructor() {
        super(`UCWinningsView`);
    }

    protected initGraphics() {
        super.initGraphics();

        this.spinsWinScreen = new UCSpinsWinScreen(this);
        this.spinsWinScreen.x = displayConfig.reelsViewWidth / 2;
        this.spinsWinScreen.y = 400;

        this.noWinsScreen = new UCNoWinScreen(this);
        this.noWinsScreen.x = displayConfig.reelsViewWidth / 2;
        this.noWinsScreen.y = 360;

        this.hawgSpinsScreen = new UCHawgSpinsScreen(this);
        this.hawgSpinsScreen.x = displayConfig.reelsViewWidth / 2;
        this.hawgSpinsScreen.y = 360;

        this.totalWinScreen = new UCTotalWinScreen(this);
        this.totalWinScreen.x = displayConfig.reelsViewWidth / 2;
        this.totalWinScreen.y = displayConfig.reelsViewHeight / 2 - 50;

        this.waysWinScreen = new UCWaysWinScreen(this);
        this.waysWinScreen.x = displayConfig.reelsViewWidth / 2;
        this.waysWinScreen.y = displayConfig.reelsViewHeight / 2 - 4;

        this.freespinIntroScreen = new UCFreespinIntroScreen(this);
        this.freespinIntroScreen.x =
            displayConfig.reelsViewWidth / 2 - this.freespinIntroScreen.width / 2;
        this.freespinIntroScreen.y =
            displayConfig.reelsViewHeight / 2 - this.freespinIntroScreen.height / 2;

        this.freespinRestoreScreen = new UCFreespinRestoreScreen(this);
        this.freespinRestoreScreen.x = this.freespinIntroScreen.x;
        this.freespinRestoreScreen.y = this.freespinIntroScreen.y;

        this.freespinOutroScreen = new UCFreespinOutroScreen(this);
        this.freespinOutroScreen.x = this.freespinIntroScreen.x;
        this.freespinOutroScreen.y = this.freespinIntroScreen.y;

        this.superFreespinIntroScreen = new UCSuperFreespinIntroScreen(this);
        this.superFreespinIntroScreen.x = this.freespinIntroScreen.x;
        this.superFreespinIntroScreen.y = this.freespinIntroScreen.y;

        this.superFreespinRestoreScreen = new UCSuperFreespinRestoreScreen(this);
        this.superFreespinRestoreScreen.x = this.freespinRestoreScreen.x;
        this.superFreespinRestoreScreen.y = this.freespinRestoreScreen.y;

        this.superFreespinOutroScreen = new UCSuperFreespinOutroScreen(this);
        this.superFreespinOutroScreen.x = this.freespinOutroScreen.x;
        this.superFreespinOutroScreen.y = this.freespinOutroScreen.y;
    }

    public showFreespinIntroScreen(onClick: Function, fsLeft: number, isSuperFs: boolean = false) {
        const introScreen = isSuperFs ? this.superFreespinIntroScreen : this.freespinIntroScreen;
        const timeline = gsap.timeline({
            onStart: () => {
                this.addChild(introScreen);
            },
        });

        introScreen.onContinueBtnClick = onClick;

        timeline.add(introScreen.getGsapAnimation(fsLeft));
    }

    public showFreespinRestoreScreen(
        fsLeft: number,
        onClick: Function,
        isSuperFs: boolean = false
    ): gsap.core.Timeline {
        const restoreScreen = isSuperFs
            ? this.superFreespinRestoreScreen
            : this.freespinRestoreScreen;
        const timeline = gsap.timeline({
            onStart: () => {
                this.addChild(restoreScreen);
            },
        });

        restoreScreen.onContinueBtnClick = onClick;

        timeline.add(restoreScreen.getGsapAnimation(fsLeft));

        return timeline;
    }

    public showFreespinOutroScreen(
        totalWin: number,
        onClick: Function,
        isSuperFs: boolean = false,
        winLevel: IWinLevel = winLevels[0],
        itShouldPlaySound: boolean = true,
        controller: FlowStateController
    ): gsap.core.Timeline {
        const outroScreen = isSuperFs ? this.superFreespinOutroScreen : this.freespinOutroScreen;
        const timeline = gsap.timeline({
            onStart: () => {
                this.addChild(outroScreen);
            },
            onComplete: () => {
                controller?.setSkipCallback(undefined);
            },
        });

        outroScreen.onContinueBtnClick = onClick;

        timeline.addLabel(`outroScreenStart`, 0);
        timeline.add(
            outroScreen.getGsapAnimation(totalWin, winLevel, itShouldPlaySound),
            `outroScreenStart`
        );
        timeline.addLabel(`outroScreenComplete`, `>-=0.2`);

        return timeline;
    }

    public showSpinsWinScreen(spinsWon: number): gsap.core.Tween | gsap.core.Timeline {
        return this.spinsWinScreen.getGsapAnimation(spinsWon);
    }

    public showNoWinScreen(): gsap.core.Tween | gsap.core.Timeline {
        return this.noWinsScreen.getGsapAnimation();
    }

    public showHawkSpinsScreen(fsLeft: number): gsap.core.Tween | gsap.core.Timeline {
        return this.hawgSpinsScreen.getGsapAnimation(fsLeft);
    }
}
